import React, { useEffect, useState } from "react";
import { Button, SelectInput, useDataProvider } from "react-admin";
import { useField } from "react-final-form";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles,
} from "@material-ui/core";
import { fetchAllData } from "../../../dataProvider";

interface IPrevRateChartInput {
  rateChartItemSource: string;
  schoolId: number;
  setLoading: (value: boolean) => void;
  excludeId?: number;
}

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
  cancelButton: {
    backgroundColor: "#eeeeee",
    color: "black",
  },
});

const PrevRateChartInputField = ({
  rateChartItemSource,
  schoolId,
  setLoading,
  excludeId = 0,
}: IPrevRateChartInput) => {
  const provider = useDataProvider();
  const classes = useStyles();

  const rateChartItemsField = useField(rateChartItemSource);
  const prevRateChartIdField = useField("prevRateChartId");

  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [confirmedId, setConfirmedId] = useState<string>("");
  const [filteredChoices, setFilteredChoices] = useState<IRateChart[]>([]);

  // Get all rate chart data for the specified school
  useEffect(() => {
    fetchAllData(
      "rateCharts",
      {
        SchoolId: schoolId,
      },
      {
        field: "id",
        order: "desc",
      },
    ).then((result) => {
      // If excludeId is provided and greater than 0, filter out that specific rate chart
      // Used to exclude the current edited rate chart in the dropdown list
      const filtered =
        excludeId && excludeId > 0
          ? result.filter((choice) => choice.id !== excludeId)
          : result;
      setFilteredChoices(filtered as IRateChart[]);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, excludeId]);

  const handleDropdownChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newId = event.target.value;

    setSelectedId(newId);
    setOpen(true); // open confirmation dialog
  };

  const handleConfirm = () => {
    if (!selectedId) return;

    setOpen(false); // close confirmation dialog
    setLoading(true);
    setConfirmedId(selectedId); // Save the selected ID as the new confirmed value

    provider
      .getOne("rateCharts", {
        id: Number(selectedId),
      })
      .then(({ data }) => {
        rateChartItemsField.input.onChange(data.rateChartItems);
        prevRateChartIdField.input.onChange(selectedId);

        setLoading(false);
        setSelectedId("");
      });
  };

  const handleCancel = () => {
    setOpen(false);

    // Revert the dropdown value to the last confirmed one
    prevRateChartIdField.input.onChange(confirmedId);

    setSelectedId("");
  };

  return (
    <>
      <SelectInput
        label="Previous Rate Chart"
        source="prevRateChartId"
        optionText="name"
        choices={filteredChoices}
        onChange={handleDropdownChange}
      />

      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="md"
      >
        <DialogTitle id="alert-dialog-title">Replace Rate Chart</DialogTitle>
        <DialogContent>
          <DialogContentText color="initial">
            Are you sure you want to replace the current rate chart? This will
            override the existing values.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            label="Cancel"
            color="secondary"
            onClick={handleCancel}
            className={classes.cancelButton}
          />
          <Button label="Confirm" color="primary" onClick={handleConfirm} />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PrevRateChartInputField;
